import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useAppDispatch } from '../../../../redux/hooks';
import { fetchSubscribedPortfolios } from '../../../../redux/modules/subscribedPortfolio/subscribedPortfolio.actions';
import { fetchUserBankInfo, fetchUserVirtualB2BHistory } from '../../../../redux/modules/user/user.actions';
import { madkhoulBankInfoSelector, userSelector } from '../../../../redux/modules/user/user.selectors';
import { BankDetailsTabList } from './BankDetailsTabList';
import { OrdersTabList } from './OrdersTabList';
import { SubscribedPortfolioTabList } from './SubscribedPortfolioTabList';
import { TransactionsTable } from './TransactionsTable';
import { TransactionsLogsTable } from './TransactionsLogsTable';
import { UserTransactionsTable } from './UserTransactionsTable';
import { UserRecurringSubscriptionsTable } from './UserRecurringSubscriptionsTable';

import { SectionCard } from '../../../../components/Section';

interface OwnedSharesTabProps {
  userId: number;
}

export function OwnedSharesTab({ userId }: OwnedSharesTabProps) {
  const dispatch = useAppDispatch();
  const { error } = useSelector(userSelector);
  const bankInfo = useSelector(madkhoulBankInfoSelector);
  useEffect(() => {
    if (userId) {
      dispatch(fetchUserBankInfo(userId));
      dispatch(
        fetchSubscribedPortfolios({
          limit: 100,
          offset: 0,
          sortField: 'id',
          sortOrder: 'desc',
          filter: JSON.stringify({
            items: [
              { columnField: 'userId', operatorValue: '=', value: userId },
              // { columnField: 'status.name', operatorValue: 'is', value: SubscribedPortfolioStatus.ACCEPTED },
            ],
          }),
          relations: ['subscribedPortfolioAssociatedIndexFund'],
        }),
      );
    }
  }, []);

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <SectionCard title="Bank details" sx={{ height: '450px' }}>
        <Box sx={{ position: 'relative', width: '100%' }}>
          {bankInfo && <BankDetailsTabList userBankInfo={bankInfo} userId={userId} />}
        </Box>
      </SectionCard>
      <SectionCard title="Subscribed portfolios" sx={{ mt: '16px' }}>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <SubscribedPortfolioTabList userId={userId} />
        </Box>
      </SectionCard>
      <SectionCard title="Orders details" sx={{ mt: '16px', height: '450px' }}>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <OrdersTabList userId={userId} />
        </Box>
      </SectionCard>
      <SectionCard
        title="Recurring subscriptions"
        disablePadding
        sx={{ mt: '16px', '& .MuiDataGrid-root': { border: 0 } }}
      >
        <UserRecurringSubscriptionsTable userId={userId} />
      </SectionCard>
      {userId && (
        <SectionCard title="User transactions" disablePadding sx={{ mt: '16px', '& .MuiDataGrid-root': { border: 0 } }}>
          <UserTransactionsTable userId={userId} />
        </SectionCard>
      )}
      {bankInfo?.id && (
        <SectionCard title="Transactions" disablePadding sx={{ mt: '16px', '& .MuiDataGrid-root': { border: 0 } }}>
          <TransactionsTable userBankId={bankInfo.id} />
        </SectionCard>
      )}
      {bankInfo?.id && (
        <SectionCard title="Transaction logs" disablePadding sx={{ mt: '16px', '& .MuiDataGrid-root': { border: 0 } }}>
          <TransactionsLogsTable userBankId={bankInfo.id} />
        </SectionCard>
      )}
    </>
  );
}
